import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';

import { cn } from '~/lib/cn';

// 'railway' variant is reserved for EN 45545 / rolling-stock contexts. Never use for general emphasis.
const badgeVariants = cva(
  // Base: label typography + pill shape
  [
    'inline-flex items-center',
    'font-display font-semibold tracking-[0.22em] uppercase text-[0.6875rem]',
    'px-2.5 py-1 rounded-sm border',
  ],
  {
    variants: {
      variant: {
        // Cyan outline — default accent
        default: [
          'bg-transparent',
          'border-[var(--color-cyan-signal)] text-[var(--color-cyan-signal)]',
        ],
        // Filled electric-blue — used for primary status/highlight
        solid: [
          'bg-[var(--color-electric-blue)] border-[var(--color-electric-blue)]',
          'text-[var(--color-frost-white)]',
        ],
        // Rail Orange outline — RESERVED for EN 45545 / rolling-stock contexts. Never use for general emphasis.
        railway: [
          'bg-transparent',
          'border-[var(--color-rail-orange)] text-[var(--color-rail-orange)]',
        ],
        // Steel-mist outline — used for "In progress" / neutral status
        status: [
          'bg-transparent',
          'border-[var(--color-steel-mist)] text-[var(--color-steel-mist)]',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type BadgeVariant = 'default' | 'solid' | 'railway' | 'status';

interface BadgeProps extends VariantProps<typeof badgeVariants> {
  variant?: BadgeVariant;
  children: React.ReactNode;
  className?: string;
}

function Badge({ variant = 'default', children, className }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)}>{children}</span>;
}

export { Badge, badgeVariants };
export type { BadgeProps, BadgeVariant };
export default Badge;
